import { AnyAction, createNextState } from '@reduxjs/toolkit';
import get from 'lodash/get';
import { Nullable, RequestState } from 'modules/types/common';
import { switchAccountSuccess } from 'store/account/actions';
import * as actions from '../actions';
import { MessageStatus, MessageSummary } from '../types';

export interface MessageFolderState {
  isFetching: boolean;
  error: Nullable<Error>;
  data: MessageSummary[];
  searchText: string;
  requestState: RequestState;
  lastUpdated: string | undefined;
}

export const getInitialMessagingFolderState = (): MessageFolderState => ({
  isFetching: false,
  error: null,
  data: [],
  searchText: '',
  requestState: RequestState.INITIALIZED,
  lastUpdated: undefined
});

const createMessagingFolderReducer = ([
  GET_CACHE_FETCH_SUCCESS,
  FETCH,
  FETCH_SUCCESS,
  FETCH_FAIL,
  APPLY_SEARCH,
  CLEAR_SEARCH,
  DETAIL_FETCHED
]: string[]) => {
  return (state = getInitialMessagingFolderState(), action: AnyAction) => {
    return createNextState(state, draft => {
      switch (action.type) {
        case FETCH: {
          draft.isFetching = true;
          draft.error = null;
          draft.requestState = RequestState.REQUESTING;
          return draft;
        }
        case GET_CACHE_FETCH_SUCCESS: {
          draft.data = action.payload.data;
          draft.lastUpdated = action.payload.cacheUpdatedAt;
          return draft;
        }
        case FETCH_SUCCESS: {
          draft.isFetching = false;
          draft.data = action.payload.data;
          draft.requestState = RequestState.SUCCESS;
          return draft;
        }
        case DETAIL_FETCHED: {
          const updatedItem = draft.data.find(
            item => item.id === get(action.payload.request, 'headers.mhp-filter-resource-id')
          );

          return {
            ...draft,
            data: draft.data.map(item => {
              if (updatedItem === item) {
                return { ...item, status: MessageStatus.OPENED };
              }

              return item;
            })
          };
        }
        case FETCH_FAIL: {
          draft.isFetching = false;
          draft.error = action.error;
          draft.requestState = RequestState.ERROR;
          return draft;
        }
        case APPLY_SEARCH: {
          draft.searchText = action.payload;
          return draft;
        }
        case CLEAR_SEARCH: {
          draft.searchText = '';
          return draft;
        }
        case switchAccountSuccess.type: {
          return getInitialMessagingFolderState();
        }
        default: {
          return state;
        }
      }
    });
  };
};

const inboxReducer = createMessagingFolderReducer([
  actions.GET_INBOX_CACHE_SUCCESS,
  actions.GET_INBOX_MESSAGES,
  actions.GET_INBOX_MESSAGES_SUCCESS,
  actions.GET_INBOX_MESSAGES_FAIL,
  actions.APPLY_INBOX_SEARCH,
  actions.CLEAR_INBOX_SEARCH,
  actions.GET_MESSAGE_BY_ID
]);

const outboxReducer = createMessagingFolderReducer([
  actions.GET_OUTBOX_CACHE_SUCCESS,
  actions.GET_OUTBOX_MESSAGES,
  actions.GET_OUTBOX_MESSAGES_SUCCESS,
  actions.GET_OUTBOX_MESSAGES_FAIL,
  actions.APPLY_OUTBOX_SEARCH,
  actions.CLEAR_OUTBOX_SEARCH
]);

export default {
  inbox: inboxReducer,
  outbox: outboxReducer
};
